/**
 * Phone — finding the telephone numbers in host-written text and turning them
 * into `tel:` links.
 *
 * The Gästemappe is free text: "Taxi Müller 035022 41234", "Notruf 112",
 * "Gastgeber: 0170 / 123 45 67". A guest in an emergency must not have to copy
 * a number off the screen, so every number becomes tappable — dialled in the
 * international form (+49 …), which works from a foreign SIM too, and shown
 * exactly as the host wrote it.
 *
 * Deliberately conservative, because a false hit dials a stranger:
 *   • a national number starts with 0, an international one with + or 00;
 *     7 to 15 digits (a German postcode has 5, so "01814 Bad Schandau" stays
 *     text); separators are spaces, "/", "-" and brackets — never "." or ":",
 *     so dates and times stay text
 *   • a number glued to a letter or to more digits ("DE89 3704 0044 …", an
 *     IBAN) is not one
 *   • the short service numbers (112, 110, 116 117) only when they are the
 *     whole value, or follow a word that announces them ("Notruf 112") — a
 *     "Zimmer 112" must stay a room
 *   • in values that are secrets or codes (Wi-Fi password, key safe, IBAN —
 *     see Phone.isExcludedLabel) the caller asks for international numbers
 *     only: "+49 171 …" is a number to text for the code, "0815 4711" may be
 *     the code itself
 *
 * No lookbehind in the patterns: Safari before 16.4 rejects it at parse time,
 * which would take the whole bundle down with it.
 */
export class Phone
{
    /** Emergency and service numbers that are dialled as they are. */
    static SHORT = [ "112", "110", "116117" ];

    // A national (0…) or international (+… / 00…) number, separators between
    // the digits. The digit count is checked afterwards.
    static LONG = /(?:\+|00)?\d[\d  /()\-]*\d/g;

    // "Notruf 112", "Polizei: 110", "Bereitschaftsdienst 116 117".
    static ANNOUNCED_SHORT = /(notruf|polizei|feuerwehr|rettungsdienst|rettung|notarzt|bereitschaftsdienst|emergency|police)(\s*[:\-–]?\s*)(112|110|116[  ]?117)(?!\d)/gi;

    // Labels whose value is a secret or a code, never a number to call.
    static EXCLUDED_LABEL = /passw|kennwort|password|code|pin\b|iban|bic|konto|schlüssel|schluessel|tresor|safe|wlan|wifi|wi-fi|ssid/i;

    static isExcludedLabel( label )
    {
        return Phone.EXCLUDED_LABEL.test( String( label || "" ) );
    }

    /**
     * The `tel:` target for a number as written, or null when it is not one.
     * "035022 41234" → "+493502241234", "+49 (0) 351 123" → "+49351123",
     * "0049 351 …" → "+49351…", "116 117" → "116117".
     */
    static toTel( raw )
    {
        const text = String( raw || "" ).trim();
        // "+49 (0) 351": the (0) is the national prefix, dropped once the
        // country code is there.
        const cleaned = text.replace( /\(\s*0\s*\)/g, "" );
        const international = /^\s*(\+|00)/.test( cleaned );
        const digits = cleaned.replace( /\D/g, "" );

        if ( Phone.SHORT.includes( digits ) )
        {
            return digits;
        }

        if ( international )
        {
            const number = cleaned.trim().startsWith( "+" ) ? digits : digits.slice( 2 );
            return number.length >= 8 && number.length <= 15 && number[ 0 ] !== "0" ? "+" + number : null;
        }

        if ( digits[ 0 ] !== "0" || digits[ 1 ] === "0" || digits.length < 7 || digits.length > 15 )
        {
            return null;
        }
        return "+49" + digits.slice( 1 );
    }

    /**
     * Split a text into plain and phone parts, in order:
     * [ { text }, { text, tel }, … ]. A text without a number comes back as a
     * single plain part.
     *
     * @param {string} text
     * @param {boolean} [whole=false] The text is one value on its own (an item
     *        value), so a bare short number such as "112" counts.
     * @param {boolean} [internationalOnly=false] Only "+…" / "00…" numbers —
     *        for texts around secrets and codes (see the class comment).
     */
    static split( text, whole = false, internationalOnly = false )
    {
        const source = String( text || "" );
        if ( !source )
        {
            return [];
        }

        if ( whole && !internationalOnly && Phone.SHORT.includes( source.replace( /[\s ]/g, "" ) ) )
        {
            return [ { text: source, tel: source.replace( /[\s ]/g, "" ) } ];
        }

        const hits = [];

        Phone.ANNOUNCED_SHORT.lastIndex = 0;
        let m;
        while ( !internationalOnly && ( m = Phone.ANNOUNCED_SHORT.exec( source ) ) !== null )
        {
            const start = m.index + m[ 1 ].length + m[ 2 ].length;
            hits.push( { start, end: start + m[ 3 ].length, tel: m[ 3 ].replace( /\D/g, "" ) } );
        }

        Phone.LONG.lastIndex = 0;
        while ( ( m = Phone.LONG.exec( source ) ) !== null )
        {
            let value = m[ 0 ];
            let start = m.index;
            const end = start + value.length;

            // "(0170) 123 4567": the pattern starts at the digit, so take the
            // opening bracket into the link with its partner.
            if ( source.charAt( start - 1 ) === "(" && value.indexOf( ")" ) > 0 && value.indexOf( "(" ) < 0 )
            {
                start--;
                value = "(" + value;
            }

            // Glued to a letter or a digit: part of something else (an IBAN
            // group "DE89 3704 …", an order number, "Zi.0112a"). A digit one
            // space before is the IBAN case too; a word one space before
            // ("Taxi 035022 …") is how numbers are normally introduced.
            const before = source.slice( Math.max( 0, start - 2 ), start );
            const after = source.charAt( end );
            if ( /[\p{L}\d]$/u.test( before ) || /\d[\s ]$/.test( before ) || /[\p{L}\d]/u.test( after ) )
            {
                continue;
            }

            // "01/11/2026" is a date, not a number in Dresden.
            if ( /^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test( value ) )
            {
                continue;
            }

            if ( internationalOnly && !/^\s*(\+|00)/.test( value ) )
            {
                continue;
            }

            const tel = Phone.toTel( value );
            if ( !tel || Phone.SHORT.includes( tel ) )
            {
                continue;
            }
            if ( hits.some( ( h ) => start < h.end && end > h.start ) )
            {
                continue;
            }
            hits.push( { start, end, tel } );
        }

        if ( hits.length === 0 )
        {
            return [ { text: source } ];
        }

        hits.sort( ( a, b ) => a.start - b.start );

        const parts = [];
        let cursor = 0;
        hits.forEach( ( hit ) =>
        {
            if ( hit.start > cursor )
            {
                parts.push( { text: source.slice( cursor, hit.start ) } );
            }
            parts.push( { text: source.slice( hit.start, hit.end ), tel: hit.tel } );
            cursor = hit.end;
        } );
        if ( cursor < source.length )
        {
            parts.push( { text: source.slice( cursor ) } );
        }
        return parts;
    }

    /** True when a text is nothing but one phone number (a pill, not a link in prose). */
    static isOnlyNumber( parts )
    {
        const meaningful = parts.filter( ( p ) => p.tel || p.text.trim() );
        return meaningful.length === 1 && !!meaningful[ 0 ].tel;
    }
}
